import React, { useState, useEffect, useRef } from 'react';
import StatusChecklistModal from './StatusChecklistModal';
import ConfigurationModal from './ConfigurationModal';
import featureFlags from '../config/versions.config';
import { API_BASE_URL, ASSET_BASE_URL } from '../config/constants';

function ServerStatus({
  onStatusChange,
  codeQualityIssues = [],
  isLoadingCodeQuality = false,
  standardsViolations = [],
  isLoadingStandards = false,
  onScanStandards,
  onAddressChange,
  // View state props
  isCodeViewActive = false,
  isDevViewActive = false,
  isChartActive = true,
  onToggleCodeView,
  onToggleDevView,
  onToggleChart,
  previewServerActive = false,
  terminalOpen = false,
  onToggleTerminal
}) {
  const [showCmdGHint, setShowCmdGHint] = useState(false);
  const [showTerminalHint, setShowTerminalHint] = useState(false);
  const [showDisconnect, setShowDisconnect] = useState(false);
  const [serverStatus, setServerStatus] = useState('checking');
  const [showKillButton, setShowKillButton] = useState(false);
  const [isKilling, setIsKilling] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
  const [rootRepoName, setRootRepoName] = useState('');

  // Bonzai server address - controls whether app UI shows
  const serverAddress = API_BASE_URL;
  
  // Preview server address (user's dev server) - editable, shown in iframe
  const [previewAddress, setPreviewAddress] = useState(() => {
    return localStorage.getItem('previewAddress') || 'http://localhost:3000';
  });
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(() => {
    return localStorage.getItem('previewAddress') || 'http://localhost:3000';
  });
  const inputRef = useRef(null);

  // Focus input when editing starts
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleAddressClick = () => {
    setEditValue(previewAddress);
    setIsEditing(true);
  };

  const handleAddressSave = () => {
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== previewAddress) {
      setPreviewAddress(trimmed);
      localStorage.setItem('previewAddress', trimmed);
      if (onAddressChange) onAddressChange(trimmed);
    }
    setIsEditing(false);
  };

  const handleAddressKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleAddressSave();
    } else if (e.key === 'Escape') {
      setEditValue(previewAddress);
      setIsEditing(false);
    }
  };

  const checkServerStatus = async () => {
    try {
      const response = await fetch(`${serverAddress}/health`, {
        method: 'GET',
        mode: 'cors'
      });

      if (response.ok) {
        // Get repo name from root endpoint
        try {
          const data = await response.json();
          if (data.repoName) {
            setRootRepoName(data.repoName);
          }
        } catch (e) {
          // Ignore errors parsing response
        }

        // Fetch and log file list
        try {
          const listResponse = await fetch(`${serverAddress}/list`);
          await listResponse.json();
        } catch (e) {
          // Ignore errors fetching file list
        }

        setServerStatus('active');
        setShowKillButton(true);
        if (onStatusChange) onStatusChange('active');
      } else {
        throw new Error('Server not responding');
      }
    } catch (error) {
      setServerStatus('inactive');
      setShowKillButton(false);
      if (onStatusChange) onStatusChange('inactive');
    }
  };

  const killServer = async () => {
    setIsKilling(true);

    try {
      const response = await fetch(`${serverAddress}/shutdown`, {
        method: 'POST',
        mode: 'cors'
      });

      setTimeout(() => {
        setServerStatus('inactive');
        setShowKillButton(false);
        setIsKilling(false);
        if (onStatusChange) onStatusChange('inactive');
      }, 1000);
    } catch (error) {
      console.error('Error killing server:', error);
      setIsKilling(false);
      setTimeout(() => {
        checkServerStatus();
      }, 500);
    }
  };

  useEffect(() => {
    checkServerStatus();
    const interval = setInterval(checkServerStatus, 5000);
    return () => clearInterval(interval);
  }, []);

  // Keyboard shortcut: Cmd+I to toggle issues modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Cmd+I (Mac) or Ctrl+I (Windows/Linux)
      if ((e.metaKey || e.ctrlKey) && e.key === 'i') {
        e.preventDefault(); // Prevent browser's default behavior
        setIsModalOpen(prev => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    if (onStatusChange) {
      onStatusChange(serverStatus);
    }
  }, [serverStatus, onStatusChange]);

  if (serverStatus === 'active') {
    // Determine status based on code quality issues + standards violations count
    const issueCount = (codeQualityIssues?.length || 0) + (standardsViolations?.length || 0);
    const isChecking = isLoadingCodeQuality || isLoadingStandards;
    const status = isChecking
      ? { text: 'Scanning for issues...', color: '#3B82F6', isLoading: true }
      : issueCount === 0
        ? null
        : { text: `${issueCount} issue${issueCount !== 1 ? 's' : ''} found`, color: '#f59e0b' };

    return (
      <>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px 16px',
        background: '#ffffff',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
        position: 'relative',
        zIndex: 1001
      }}>
        {featureFlags.showV3 ? (
          /* V3 Mode: Repo + Disconnect on left, tabs in center, terminal toggle on right */
          <>
            {/* Left: Logo + Repo info + Disconnect on hover */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              {/* Bonzai Logo - links to bonzai.dev */}
              <a
                href="https://bonzai.dev"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  textDecoration: 'none'
                }}
              >
                <img
                  src={`${ASSET_BASE_URL}/favicon.jpg`}
                  alt="Bonzai"
                  style={{
                    width: '28px',
                    height: '28px',
                    borderRadius: '6px',
                    transition: 'opacity 0.15s ease'
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.8'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
                />
              </a>
              {/* Repo info */}
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#333', cursor: 'default' }}
                onMouseEnter={() => setShowDisconnect(true)}
                onMouseLeave={() => setShowDisconnect(false)}
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" style={{ width: '18px', height: '18px', color: '#28a745' }}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                </svg>
                <span>connected to repo "<strong>{rootRepoName || '...'}</strong>"</span>
              <svg
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!isKilling) killServer();
                }}
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 1024 1024"
                fill="#e74c3c"
                style={{
                  width: '16px',
                  height: '16px',
                  cursor: isKilling ? 'not-allowed' : 'pointer',
                  opacity: showDisconnect ? 1 : 0,
                  transition: 'opacity 0.15s ease',
                  pointerEvents: showDisconnect ? 'auto' : 'none'
                }}
              >
                <title>Disconnect listener server</title>
                <path d="M917.7 148.8l-42.4-42.4c-1.6-1.6-3.6-2.3-5.7-2.3s-4.1.8-5.7 2.3l-76.1 76.1a199.27 199.27 0 0 0-112.1-34.3c-51.2 0-102.4 19.5-141.5 58.6L432.3 308.7a8.03 8.03 0 0 0 0 11.3L704 591.7c1.6 1.6 3.6 2.3 5.7 2.3 2 0 4.1-.8 5.7-2.3l101.9-101.9c68.9-69 77-175.7 24.3-253.5l76.1-76.1c3.1-3.2 3.1-8.3 0-11.4zM769.1 441.7l-59.4 59.4-186.8-186.8 59.4-59.4c24.9-24.9 58.1-38.7 93.4-38.7 35.3 0 68.4 13.7 93.4 38.7 24.9 24.9 38.7 58.1 38.7 93.4 0 35.3-13.8 68.4-38.7 93.4zm-190.2 105a8.03 8.03 0 0 0-11.3 0L501 613.3 410.7 523l66.7-66.7c3.1-3.1 3.1-8.2 0-11.3L441 408.6a8.03 8.03 0 0 0-11.3 0L363 475.3l-43-43a7.85 7.85 0 0 0-5.7-2.3c-2 0-4.1.8-5.7 2.3L206.8 534.2c-68.9 69-77 175.7-24.3 253.5l-76.1 76.1a8.03 8.03 0 0 0 0 11.3l42.4 42.4c1.6 1.6 3.6 2.3 5.7 2.3s4.1-.8 5.7-2.3l76.1-76.1c33.7 22.9 72.9 34.3 112.1 34.3 51.2 0 102.4-19.5 141.5-58.6l101.9-101.9c3.1-3.1 3.1-8.2 0-11.3l-43-43 66.7-66.7c3.1-3.1 3.1-8.2 0-11.3l-36.6-36.2zM441.7 769.1a131.32 131.32 0 0 1-93.4 38.7c-35.3 0-68.4-13.7-93.4-38.7a131.32 131.32 0 0 1-38.7-93.4c0-35.3 13.7-68.4 38.7-93.4l59.4-59.4 186.8 186.8-59.4 59.4z"/>
              </svg>
              </div>
            </div>
            {/* Center: View Toggle Buttons */}
            <div
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px', position: 'absolute', left: '50%', transform: 'translateX(-50%)' }}
              onMouseEnter={() => setShowCmdGHint(true)}
              onMouseLeave={() => setShowCmdGHint(false)}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              {/* Chart Toggle */}
              <button
                onClick={onToggleChart}
                style={{
                  background: isChartActive ? '#e8f4ff' : 'transparent',
                  border: isChartActive ? '1px solid #3B82F6' : '1px solid #e0e0e0',
                  borderRadius: '6px',
                  padding: '5px 10px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                  fontSize: '12px',
                  color: isChartActive ? '#3B82F6' : '#666',
                  fontWeight: isChartActive ? '500' : '400',
                  transition: 'all 0.15s ease'
                }}
                onMouseEnter={(e) => {
                  if (!isChartActive) {
                    e.currentTarget.style.background = '#f5f5f5';
                    e.currentTarget.style.borderColor = '#d0d0d0';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isChartActive) {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.borderColor = '#e0e0e0';
                  }
                }}
                title="Toggle Chart View"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" style={{ width: '14px', height: '14px' }}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 14.25v2.25m3-4.5v4.5m3-6.75v6.75m3-9v9M6 20.25h12A2.25 2.25 0 0 0 20.25 18V6A2.25 2.25 0 0 0 18 3.75H6A2.25 2.25 0 0 0 3.75 6v12A2.25 2.25 0 0 0 6 20.25Z" />
                </svg>
                Chart
              </button>

              {/* Code Toggle - COMMENTED OUT
              <button
                onClick={onToggleCodeView}
                style={{
                  background: isCodeViewActive ? '#e8f4ff' : 'transparent',
                  border: isCodeViewActive ? '1px solid #3B82F6' : '1px solid #e0e0e0',
                  borderRadius: '6px',
                  padding: '5px 10px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                  fontSize: '12px',
                  color: isCodeViewActive ? '#3B82F6' : '#666',
                  fontWeight: isCodeViewActive ? '500' : '400',
                  transition: 'all 0.15s ease'
                }}
                onMouseEnter={(e) => {
                  if (!isCodeViewActive) {
                    e.currentTarget.style.background = '#f5f5f5';
                    e.currentTarget.style.borderColor = '#d0d0d0';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isCodeViewActive) {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.borderColor = '#e0e0e0';
                  }
                }}
                title="Toggle Code View"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" style={{ width: '14px', height: '14px' }}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 6.75 22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3-4.5 16.5" />
                </svg>
                Code
              </button>
              */}

              {/* Preview Toggle */}
              <button
                onClick={onToggleDevView}
                style={{
                  background: isDevViewActive ? '#e8f4ff' : 'transparent',
                  border: isDevViewActive ? '1px solid #3B82F6' : '1px solid #e0e0e0',
                  borderRadius: '6px',
                  padding: '5px 10px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                  fontSize: '12px',
                  color: isDevViewActive ? '#3B82F6' : previewServerActive ? '#666' : '#bbb',
                  fontWeight: isDevViewActive ? '500' : '400',
                  transition: 'all 0.15s ease',
                  opacity: previewServerActive || isDevViewActive ? 1 : 0.6
                }}
                onMouseEnter={(e) => {
                  if (!isDevViewActive) {
                    e.currentTarget.style.background = '#f5f5f5';
                    e.currentTarget.style.borderColor = '#d0d0d0';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isDevViewActive) {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.borderColor = '#e0e0e0';
                  }
                }}
                title={previewServerActive ? "Toggle Preview" : "Preview (no server connected)"}
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" style={{ width: '14px', height: '14px' }}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                </svg>
                Preview
              </button>

              </div>
              {/* Cmd+G hint - shows on hover, right of buttons */}
              <span style={{
                position: 'absolute',
                left: '100%',
                top: '50%',
                transform: 'translateY(-50%)',
                marginLeft: '8px',
                fontSize: '11px',
                color: '#e74c3c',
                fontFamily: 'monospace',
                opacity: showCmdGHint ? 1 : 0,
                pointerEvents: 'none',
                transition: 'opacity 0.15s ease',
                whiteSpace: 'nowrap'
              }}>
                <code style={{ background: '#fee2e2', padding: '1px 4px', borderRadius: '3px', fontSize: '10px' }}>⌘G</code> to switch views
              </span>
            </div>
            {/* Right: Terminal toggle icon with hover hint */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{
                fontSize: '11px',
                color: '#e74c3c',
                fontFamily: 'monospace',
                opacity: showTerminalHint ? 1 : 0,
                transition: 'opacity 0.15s ease',
                whiteSpace: 'nowrap'
              }}>
                <code style={{ background: '#fee2e2', padding: '1px 4px', borderRadius: '3px', fontSize: '10px' }}>⌘B</code> to {terminalOpen ? 'hide' : 'show'} terminal
              </span>
              <button
                onClick={onToggleTerminal}
                style={{
                  background: terminalOpen ? '#e8f4ff' : 'transparent',
                  border: terminalOpen ? '1px solid #3B82F6' : '1px solid #e0e0e0',
                  borderRadius: '6px',
                  padding: '6px 8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: terminalOpen ? '#3B82F6' : '#666',
                  transition: 'all 0.15s ease'
                }}
                onMouseEnter={(e) => {
                  setShowTerminalHint(true);
                  if (!terminalOpen) {
                    e.currentTarget.style.background = '#f5f5f5';
                    e.currentTarget.style.borderColor = '#d0d0d0';
                  }
                }}
                onMouseLeave={(e) => {
                  setShowTerminalHint(false);
                  if (!terminalOpen) {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.borderColor = '#e0e0e0';
                  }
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" style={{ width: '18px', height: '18px' }}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 7.5l3 2.25-3 2.25m4.5 0h3m-9 8.25h13.5A2.25 2.25 0 0021 18V6a2.25 2.25 0 00-2.25-2.25H5.25A2.25 2.25 0 003 6v12a2.25 2.25 0 002.25 2.25z" />
                </svg>
              </button>
            </div>
          </>
        ) : (
          /* Original Mode: Show preview address */
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '14px',
            color: '#333',
            flex: 1
          }}>
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" style={{ width: '18px', height: '18px', color: '#28a745' }}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
            </svg>
            <span>previewing</span>
            {isEditing ? (
              <input
                ref={inputRef}
                type="text"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onBlur={handleAddressSave}
                onKeyDown={handleAddressKeyDown}
                style={{
                  padding: '2px 6px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#333',
                  border: '1px solid #3B82F6',
                  borderRadius: '4px',
                  outline: 'none',
                  minWidth: '180px',
                  fontFamily: 'inherit'
                }}
              />
            ) : (
              <strong
                onClick={handleAddressClick}
                style={{
                  color: '#333',
                  cursor: 'pointer',
                  padding: '2px 6px',
                  borderRadius: '4px',
                  transition: 'background 0.15s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#f0f0f0';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                }}
                title="Click to edit preview address"
              >
                {previewAddress}
              </strong>
            )}
            <button
              onClick={() => {
                const cleared = 'http://localhost:';
                setPreviewAddress(cleared);
                setEditValue(cleared);
                localStorage.setItem('previewAddress', cleared);
                if (onAddressChange) onAddressChange(cleared);
              }}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: '2px 4px',
                marginLeft: '4px',
                color: '#999',
                fontSize: '14px',
                lineHeight: '1',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '3px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#666';
                e.currentTarget.style.background = '#f0f0f0';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = '#999';
                e.currentTarget.style.background = 'transparent';
              }}
              title="Clear port"
            >
              ×
            </button>
          </div>
        )}
        
        {/* Center status button - only show when there are issues or scanning */}
        {status && (
          <button
            onClick={() => setIsModalOpen(true)}
            style={{
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
              background: '#f5f5f5',
              border: '1px solid #e0e0e0',
              cursor: 'pointer',
              color: '#333',
              fontSize: '13px',
              fontWeight: '400',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
              padding: '6px 12px',
              borderRadius: '6px',
              transition: 'all 0.15s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              boxShadow: 'none',
              zIndex: 1002
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#eeeeee';
              e.currentTarget.style.borderColor = '#d0d0d0';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#f5f5f5';
              e.currentTarget.style.borderColor = '#e0e0e0';
            }}
          >
            {status.isLoading ? (
              <div
                style={{
                  width: '12px',
                  height: '12px',
                  border: '2px solid #e0e0e0',
                  borderTopColor: '#3B82F6',
                  borderRadius: '50%',
                  flexShrink: 0,
                  animation: 'spin 0.8s linear infinite'
                }}
              />
            ) : (
              <div
                style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: status.color,
                  flexShrink: 0
                }}
              />
            )}
            <span>{status.text}</span>
            <style>{`
              @keyframes spin {
                to { transform: rotate(360deg); }
              }
            `}</style>
          </button>
        )}

        {showKillButton && !featureFlags.showV3 && (
          <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '16px' }}>
            {/* Configuration Button */}
            <button
              onClick={() => setIsConfigModalOpen(true)}
              title="Configuration"
              style={{
                background: 'transparent',
                border: '1px solid #e0e0e0',
                borderRadius: '6px',
                padding: '6px 10px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '12px',
                color: '#666',
                transition: 'all 0.15s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#f5f5f5';
                e.currentTarget.style.borderColor = '#d0d0d0';
                e.currentTarget.style.color = '#333';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.borderColor = '#e0e0e0';
                e.currentTarget.style.color = '#666';
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" style={{ width: '16px', height: '16px' }}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28Z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
              </svg>
              Config
            </button>
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                killServer();
              }}
              disabled={isKilling}
              onMouseEnter={(e) => {
                if (!isKilling) {
                  e.target.style.background = '#c0392b';
                }
              }}
              onMouseLeave={(e) => {
                if (!isKilling) {
                  e.target.style.background = '#e74c3c';
                }
              }}
              style={{
                background: '#e74c3c',
                color: 'white',
                border: 'none',
                padding: '6px 12px',
                borderRadius: '4px',
                cursor: isKilling ? 'not-allowed' : 'pointer',
                fontSize: '12px',
                whiteSpace: 'nowrap',
                opacity: isKilling ? 0.6 : 1,
                fontWeight: '500',
                position: 'relative',
                zIndex: 1002,
                transition: 'background-color 0.15s ease',
                pointerEvents: isKilling ? 'none' : 'auto'
              }}
            >
              {isKilling ? 'Disconnecting...' : 'Disconnect'}
            </button>
          </div>
        )}
      </div>
      <StatusChecklistModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        issues={codeQualityIssues}
        standardsViolations={standardsViolations}
        isLoadingStandards={isLoadingStandards}
        onScanStandards={onScanStandards}
      />
      <ConfigurationModal
        isOpen={isConfigModalOpen}
        onClose={() => setIsConfigModalOpen(false)}
      />
    </>
    );
  }

  return null;
}

export default ServerStatus;

